import React, { useCallback, useEffect, useMemo, useState } from 'react';

import './AdminUsers.css';

const API_BASE = 'http://localhost:5001';

function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');

  const { token, currentUserId } = useMemo(() => {
    if (typeof window === 'undefined') {
      return { token: null, currentUserId: null };
    }
    const storedToken = localStorage.getItem('token');
    if (!storedToken) {
      return { token: null, currentUserId: null };
    }
    try {
      const payload = JSON.parse(atob(storedToken.split('.')[1]));
      return { token: storedToken, currentUserId: payload.id || null };
    } catch (err) {
      console.error('Impossible de décoder le token :', err);
      return { token: storedToken, currentUserId: null };
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    if (!token) {
      setError('Authentification requise pour consulter les utilisateurs.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/users`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      let payload = null;
      try {
        payload = await res.json();
      } catch {
        payload = null;
      }
      if (!res.ok) {
        throw new Error(payload?.error || 'Impossible de récupérer les utilisateurs.');
      }
      const normalized = Array.isArray(payload) ? payload.map((entry) => ({ ...entry, status: entry.status || 'approved' })) : [];
      setUsers(normalized);
    } catch (err) {
      setError(err.message || 'Erreur inattendue lors du chargement des utilisateurs.');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleToggleAdmin = async (user) => {
    if (!token) {
      return;
    }
    const nextStatus = !user.isAdmin;
    setError('');
    setFeedback('');
    try {
      const res = await fetch(`${API_BASE}/api/users/${user._id}/role`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ isAdmin: nextStatus })
      });
      let payload = null;
      try {
        payload = await res.json();
      } catch {
        payload = null;
      }
      if (!res.ok || !payload) {
        throw new Error(payload?.error || 'Impossible de modifier les droits.');
      }
      setUsers((prev) => prev.map((item) => (item._id === user._id ? { ...payload, status: payload.status || item.status || 'approved' } : item)));
      setFeedback(
        nextStatus
          ? `${payload.email} dispose désormais des droits administrateur.`
          : `${payload.email} n'est plus administrateur.`
      );
    } catch (err) {
      setError(err.message || 'Erreur lors de la mise à jour des droits.');
    }
  };

  const handleDelete = async (user) => {
    if (!token) {
      return;
    }
    const confirmed = window.confirm(`Supprimer le compte ${user.email} ?`);
    if (!confirmed) {
      return;
    }

    setError('');
    setFeedback('');
    try {
      const res = await fetch(`${API_BASE}/api/users/${user._id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (!res.ok) {
        let payload = null;
        try {
          payload = await res.json();
        } catch {
          payload = null;
        }
        throw new Error(payload?.error || 'Impossible de supprimer cet utilisateur.');
      }
      setUsers((prev) => prev.filter((item) => item._id !== user._id));
      setFeedback(`${user.email} a été supprimé.`);
    } catch (err) {
      setError(err.message || "Erreur lors de la suppression de l'utilisateur.");
    }
  };

  const handleApprove = async (user) => {
    if (!token) {
      return;
    }

    setError('');
    setFeedback('');
    try {
      const res = await fetch(`${API_BASE}/api/users/${user._id}/approve`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      let payload = null;
      try {
        payload = await res.json();
      } catch {
        payload = null;
      }
      if (!res.ok || !payload) {
        throw new Error(payload?.error || 'Impossible de valider ce compte.');
      }

      setUsers((prev) => prev.map((item) => (item._id === user._id ? { ...payload, status: payload.status || 'approved' } : item)));
      setFeedback('Le compte utilisateur a été validé.');
    } catch (err) {
      setError(err.message || 'Erreur lors de la validation du compte.');
    }
  };

  return (
    <div className="admin-users">
      <header className="admin-users__header">
        <div>
          <h3>Gestion des utilisateurs</h3>
          <p>Consultez les sociétés inscrites, attribuez les droits administrateur et supprimez les comptes inactifs.</p>
        </div>
        <div className="admin-users__metrics">
          <span className="admin-users__metric">
            <strong>{users.filter((user) => (user.status || 'approved') === 'pending').length}</strong>
            <span>En attente</span>
          </span>
          <span className="admin-users__metric">
            <strong>{users.filter((user) => user.isAdmin).length}</strong>
            <span>Administrateurs</span>
          </span>
          <span className="admin-users__metric">
            <strong>{users.length}</strong>
            <span>Total comptes</span>
          </span>
        </div>
      </header>

      {feedback && <p className="admin-users__message admin-users__message--success">{feedback}</p>}
      {error && <p className="admin-users__message admin-users__message--error">{error}</p>}

      {loading ? (
        <p>Chargement des utilisateurs...</p>
      ) : users.length === 0 ? (
        <p>Aucun utilisateur inscrit pour le moment.</p>
      ) : (
        <div className="admin-users__table-wrapper">
          <table className="admin-users__table">
            <thead>
              <tr>
                <th>Entreprise</th>
                <th>Email</th>
                <th>Numéro de téléphone</th>
                <th>Statut du compte</th>
                <th>Droits</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => {
                const isCurrentAdmin = user._id === currentUserId;
                const phone = [user.phoneCountryCode, user.phoneNumber].filter(Boolean).join(' ');
                return (
                  <tr key={user._id}>
                    <td data-label="Entreprise">
                      <span className="admin-users__company">{user.companyName}</span>
                      <span className="admin-users__kbis">{user.kbis}</span>
                    </td>
                    <td data-label="Email">{user.email}</td>
                    <td data-label="Numéro de téléphone">
                      {phone ? (
                        <span className="admin-users__phone">{phone}</span>
                      ) : (
                        <span className="admin-users__phone admin-users__phone--muted">Non renseigné</span>
                      )}
                    </td>
                    <td data-label="Statut du compte">
                      <span className={`admin-users__badge${(user.status || 'approved') === 'approved' ? ' admin-users__badge--approved' : ' admin-users__badge--pending'}`}>{(user.status || 'approved') === 'approved' ? 'Validé' : 'En attente'}</span>
                    </td>
                    <td data-label="Droits">
                      <span className={`admin-users__badge admin-users__badge--role${user.isAdmin ? ' admin-users__badge--admin-role' : ''}`}>{user.isAdmin ? 'Administrateur' : 'Utilisateur'}</span>
                      <button
                        type="button"
                        className="admin-users__action-btn"
                        onClick={() => handleToggleAdmin(user)}
                        disabled={isCurrentAdmin}
                      >
                        {user.isAdmin ? 'Retirer les droits' : 'Donner les droits'}
                      </button>
                      {isCurrentAdmin && (
                        <span className="admin-users__hint">Vous ne pouvez pas modifier vos propres droits.</span>
                      )}
                    </td>
                    <td data-label="Actions">
                      <div className="admin-users__actions">
                        <button
                          type="button"
                          className="admin-users__action-btn admin-users__action-btn--danger"
                          onClick={() => handleDelete(user)}
                          disabled={isCurrentAdmin}
                        >
                          Supprimer
                        </button>
                        {(user.status || 'approved') !== 'approved' && (
                          <button
                            type="button"
                            className="admin-users__action-btn admin-users__action-btn--success"
                            onClick={() => handleApprove(user)}
                          >
                            Valider
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default AdminUsers;
